import React from "react";
import styled from "styled-components";
import { RecentSearch } from "../../hooks/useRecentSearches";

const DropdownContainer = styled.div<{ isVisible: boolean }>`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-top: none;
    border-radius: 0 0 8px 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    max-height: 300px;
    overflow-y: auto;
    display: ${props => props.isVisible ? 'block' : 'none'};
`;

const DropdownHeader = styled.div`
    padding: 12px 16px 8px;
    font-size: 12px;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SearchItem = styled.div`
    padding: 12px 16px;
    cursor: pointer;
    color: #333;
    border-bottom: 1px solid #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    
    &:hover {
        background-color: #f8f9fa;
    }
    
    &:last-child {
        border-bottom: none;
    }
`;

const SearchQuery = styled.span`
    flex: 1;
    margin-left: 8px;
`;

const RemoveButton = styled.button`
    background: none;
    border: none;
    color: #999;
    cursor: pointer;
    padding: 4px;
    font-size: 12px;
    opacity: 0;
    transition: opacity 0.2s ease;
    
    ${SearchItem}:hover & {
        opacity: 1;
    }
    
    &:hover {
        color: #e74c3c;
    }
`;

const ClearButton = styled.button`
    background: none;
    border: none;
    color: #999;
    cursor: pointer;
    font-size: 12px;
    padding: 0;
    
    &:hover {
        color: #e74c3c;
    }
`;

const EmptyState = styled.div`
    padding: 20px 16px;
    text-align: center;
    color: #999;
    font-size: 14px;
`;

const SearchIcon = styled.i`
    color: #999;
    font-size: 12px;
    width: 16px;
`;

interface RecentSearchesProps {
    recentSearches: RecentSearch[];
    isVisible: boolean;
    onSearchSelect: (query: string) => void;
    onSearchRemove: (query: string) => void;
    onClearAll: () => void;
}

export const RecentSearchesDropdown: React.FC<RecentSearchesProps> = ({
    recentSearches,
    isVisible,
    onSearchSelect,
    onSearchRemove,
    onClearAll
}) => {
    const handleSearchClick = (query: string) => {
        onSearchSelect(query);
    };

    const handleRemoveClick = (e: React.MouseEvent, query: string) => {
        e.stopPropagation();
        onSearchRemove(query);
    };

    const handleClearClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onClearAll();
    };

    if (!isVisible) {
        return null;
    }

    return (
        <DropdownContainer isVisible={isVisible}>
            {recentSearches.length > 0 ? (
                <>
                    <DropdownHeader>
                        Letzte Suchen
                        <ClearButton 
                            onClick={handleClearClick}
                            title="Alle löschen"
                        >
                            Alle löschen
                        </ClearButton>
                    </DropdownHeader>
                    {recentSearches.map((search, index) => (
                        <SearchItem
                            key={`${search.query}-${search.timestamp}`}
                            onClick={() => handleSearchClick(search.query)}
                        >
                            <SearchIcon className="fa fa-search" />
                            <SearchQuery>{search.query}</SearchQuery>
                            <RemoveButton
                                onClick={(e) => handleRemoveClick(e, search.query)}
                                title="Entfernen"
                            >
                                <i className="fa fa-times" />
                            </RemoveButton>
                        </SearchItem>
                    ))}
                </>
            ) : (
                <EmptyState>
                    Keine letzten Suchen vorhanden
                </EmptyState>
            )}
        </DropdownContainer>
    );
};
